import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useDashboard } from "../Contexts/dashboardHooks";
import { useSandboxAIChat } from "./useSandboxAIChat";
import type { SandboxAIMessage, SandboxAIStatus, TurnSnapshot } from "./types";

export interface SandboxAIContextValue {
  open: boolean;
  status: SandboxAIStatus;
  messages: SandboxAIMessage[];
  turnSnapshot: TurnSnapshot | null;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  sendMessage: (text: string) => void;
  cancelTurn: () => void;
  keepChanges: () => void;
  undoChanges: () => void;
  retryLast: () => void;
}

const SandboxAIContext = createContext<SandboxAIContextValue | null>(null);

export function SandboxAIProvider({ children }: { children: ReactNode }) {
  const { state, applyServerSpec, restoreDashboardState, renameDashboard } = useDashboard();

  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<SandboxAIStatus>("idle");
  const [messages, setMessages] = useState<SandboxAIMessage[]>([]);
  const [conversationId, setConversationId] = useState<number | string | null>(null);
  const [turnSnapshot, setTurnSnapshot] = useState<TurnSnapshot | null>(null);

  // Refs so the async chat handlers read the latest values, not stale closures.
  const stateRef = useRef(state);
  stateRef.current = state;
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  const turnSnapshotRef = useRef(turnSnapshot);
  turnSnapshotRef.current = turnSnapshot;

  const pushMessage = useCallback(
    (message: SandboxAIMessage) => setMessages((prev) => [...prev, message]),
    [],
  );

  const { sendMessage, cancelTurn, retryLast } = useSandboxAIChat({
    getDashboardState: () => stateRef.current,
    applyServerSpec,
    renameDashboard,
    getConversationId: () => conversationIdRef.current,
    setConversationId,
    pushMessage,
    setStatus,
    setTurnSnapshot,
  });

  // Conversation, messages and snapshot are scoped per dashboard — reset on switch.
  const dashboardId = state.dashboardId;
  const prevDashboardIdRef = useRef(dashboardId);
  useEffect(() => {
    if (prevDashboardIdRef.current === dashboardId) return;
    prevDashboardIdRef.current = dashboardId;
    cancelTurn();
    setMessages([]);
    setConversationId(null);
    setTurnSnapshot(null);
    setStatus("idle");
  }, [dashboardId, cancelTurn]);

  const openPanel = useCallback(() => setOpen(true), []);
  const closePanel = useCallback(() => setOpen(false), []);
  const togglePanel = useCallback(() => setOpen((o) => !o), []);
  const keepChanges = useCallback(() => setTurnSnapshot(null), []);
  const undoChanges = useCallback(() => {
    const snapshot = turnSnapshotRef.current;
    if (!snapshot) return;
    restoreDashboardState(snapshot.widgets, snapshot.layout);
    setTurnSnapshot(null);
  }, [restoreDashboardState]);

  const value = useMemo<SandboxAIContextValue>(
    () => ({
      open, status, messages, turnSnapshot,
      openPanel, closePanel, togglePanel,
      sendMessage, cancelTurn, keepChanges, undoChanges, retryLast,
    }),
    [
      open, status, messages, turnSnapshot,
      openPanel, closePanel, togglePanel,
      sendMessage, cancelTurn, keepChanges, undoChanges, retryLast,
    ],
  );

  return <SandboxAIContext.Provider value={value}>{children}</SandboxAIContext.Provider>;
}

export function useSandboxAI(): SandboxAIContextValue {
  const ctx = useContext(SandboxAIContext);
  if (!ctx) throw new Error("useSandboxAI must be used within a SandboxAIProvider");
  return ctx;
}
